import React, { type JSX, useEffect, useRef, useState } from 'react';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import {
  getReceiptState,
  isReceiptFeedbackComplete,
  type Receipt,
  type ReceiptFeedbackLocation,
  ReceiptFeedbackRating,
  type ReceiptFeedbackReason,
  RECEIPT_FEEDBACK_REASON_LABELS,
  RECEIPT_FEEDBACK_REASON_ORDER,
  receiptsActions,
  trackReceiptEvent,
} from 'app-components/flux/receipts';

import Feedback, {
  type FeedbackDetails,
  type FeedbackLabels,
  type FeedbackReasonOption,
} from 'components/Feedback/Feedback';
import { useAppDispatch, useAppSelector } from 'hooks/redux';

const FEEDBACK_COMMENT_MAX_LENGTH = 500;

const RECEIPT_FEEDBACK_REASONS: ReadonlyArray<FeedbackReasonOption<ReceiptFeedbackReason>> =
  RECEIPT_FEEDBACK_REASON_ORDER.map((reason) => ({
    value: reason,
    label: RECEIPT_FEEDBACK_REASON_LABELS[reason],
    requiresComment: reason === 'OTHER',
  }));

const RECEIPT_FEEDBACK_LABELS: FeedbackLabels = {
  sectionAriaLabel: 'Receipt feedback',
  loading: 'Loading feedback…',
  prompt: 'Did we get this right?',
  positiveAriaLabel: 'This receipt is correct',
  negativeAriaLabel: 'This receipt has errors',
  negativeTitle: 'Oops! Looks like we missed something.',
  negativeDescription: 'Help us improve by selecting what was missing or incorrect.',
  commentLabel: 'Tell us what was missing or incorrect',
  commentDisclaimer: "Please don't include personal or sensitive information.",
  submit: 'Submit feedback',
  success: 'Thank you for your feedback.',
  saveError: 'We couldn’t save your feedback. Please try again.',
};

type ReceiptFeedbackViewState =
  | { view: 'loading' }
  | { view: 'hidden' }
  | { view: 'rating' }
  | { view: 'details'; reasons: ReceiptFeedbackReason[]; comment: string };

type Props = {
  receipt: Receipt;
  location: ReceiptFeedbackLocation;
};

const getKnownFeedbackViewState = (feedback: Receipt['feedback']): ReceiptFeedbackViewState | undefined => {
  if (feedback === undefined) return undefined;
  if (feedback === null) return { view: 'rating' };
  if (isReceiptFeedbackComplete(feedback)) return { view: 'hidden' };

  return { view: 'details', reasons: feedback.reasons, comment: feedback.comment ?? '' };
};

const getFeedbackAnalyticsContext = (receipt: Receipt, location: ReceiptFeedbackLocation) => ({
  receiptId: receipt.id,
  location,
  receiptStatus: receipt.status,
  receiptState: getReceiptState(receipt),
  ...(receipt.extractionEngine ? { extractionEngine: receipt.extractionEngine } : {}),
  isAttached: Boolean(receipt.transactionId),
});

const ReceiptFeedbackContent = ({ receipt, location }: Props): JSX.Element | null => {
  const dispatch = useAppDispatch();
  const activeReceiptIdRef = useRef<string | undefined>(undefined);
  const activeInteractionReceiptIdRef = useRef<string | undefined>(undefined);
  const completedReceiptIdRef = useRef<string | undefined>(undefined);
  const [viewState, setViewState] = useState<ReceiptFeedbackViewState>({ view: 'loading' });

  useEffect(() => {
    const isNewReceipt = activeReceiptIdRef.current !== receipt.id;
    if (isNewReceipt) {
      activeReceiptIdRef.current = receipt.id;
      activeInteractionReceiptIdRef.current = undefined;
      completedReceiptIdRef.current = undefined;
    } else if (completedReceiptIdRef.current === receipt.id) {
      return undefined;
    } else if (activeInteractionReceiptIdRef.current === receipt.id) {
      return undefined;
    }

    let isCurrent = true;
    setViewState({ view: 'loading' });

    void dispatch(receiptsActions.fetchReceiptDetail(receipt.id))
      .unwrap()
      .then((detail) => {
        if (!isCurrent) return;

        setViewState(getKnownFeedbackViewState(detail.feedback) ?? { view: 'hidden' });
      })
      .catch(() => {
        if (isCurrent) setViewState({ view: 'hidden' });
      });

    return () => {
      isCurrent = false;
    };
  }, [dispatch, receipt.id]);

  const handlePositive = async (): Promise<void> => {
    activeInteractionReceiptIdRef.current = receipt.id;
    let updatedReceipt: Receipt;
    try {
      updatedReceipt = await dispatch(
        receiptsActions.submitReceiptFeedback({ id: receipt.id, rating: ReceiptFeedbackRating.POSITIVE }),
      ).unwrap();
    } catch (error) {
      activeInteractionReceiptIdRef.current = undefined;
      throw error;
    }
    trackReceiptEvent({
      event: 'receiptFeedbackPositive',
      properties: { ...getFeedbackAnalyticsContext(updatedReceipt, location), feedbackStage: 'rating' },
    });
  };

  const handleNegative = async (): Promise<void> => {
    activeInteractionReceiptIdRef.current = receipt.id;
    let updatedReceipt: Receipt;
    try {
      updatedReceipt = await dispatch(
        receiptsActions.submitReceiptFeedback({ id: receipt.id, rating: ReceiptFeedbackRating.NEGATIVE, reasons: [] }),
      ).unwrap();
    } catch (error) {
      activeInteractionReceiptIdRef.current = undefined;
      throw error;
    }
    trackReceiptEvent({
      event: 'receiptFeedbackNegative',
      properties: {
        ...getFeedbackAnalyticsContext(updatedReceipt, location),
        feedbackStage: 'rating',
        failureReasons: [],
      },
    });
  };

  const handleSubmitDetails = async ({ reasons, comment }: FeedbackDetails<ReceiptFeedbackReason>): Promise<void> => {
    activeInteractionReceiptIdRef.current = receipt.id;
    let updatedReceipt: Receipt;
    try {
      updatedReceipt = await dispatch(
        receiptsActions.submitReceiptFeedback({
          id: receipt.id,
          rating: ReceiptFeedbackRating.NEGATIVE,
          reasons,
          ...(comment !== undefined ? { comment } : {}),
        }),
      ).unwrap();
    } catch (error) {
      activeInteractionReceiptIdRef.current = undefined;
      throw error;
    }
    trackReceiptEvent({
      event: 'receiptFeedbackNegative',
      properties: {
        ...getFeedbackAnalyticsContext(updatedReceipt, location),
        feedbackStage: 'details',
        failureReasons: reasons,
        ...(comment !== undefined ? { failureComment: comment } : {}),
        reasonCount: reasons.length,
        hasComment: comment !== undefined,
      },
    });
  };

  if (viewState.view === 'loading' || viewState.view === 'hidden') return null;

  return (
    <Feedback
      key={`${receipt.id}-${viewState.view}`}
      initialView={viewState.view}
      reasons={RECEIPT_FEEDBACK_REASONS}
      initialSelectedReasons={viewState.view === 'details' ? viewState.reasons : undefined}
      initialComment={viewState.view === 'details' ? viewState.comment : undefined}
      commentMaxLength={FEEDBACK_COMMENT_MAX_LENGTH}
      labels={RECEIPT_FEEDBACK_LABELS}
      onPositive={handlePositive}
      onNegative={handleNegative}
      onSubmitDetails={handleSubmitDetails}
      onComplete={() => {
        completedReceiptIdRef.current = receipt.id;
      }}
      sx={{ mt: 2 }}
    />
  );
};

const ReceiptFeedback = (props: Props): JSX.Element | null => {
  const feedbackEnabled = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlag(state, 'receiptsExtractionFeedbackRelease'),
  );

  return feedbackEnabled ? <ReceiptFeedbackContent {...props} /> : null;
};

export default ReceiptFeedback;
